import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Edit, Check, Sparkles, Target, Brain, Palette, TrendingUp, ArrowRight } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

export default function ProjectSetupFlow({ projectIdea, onClose, onStartProject }) {
  const { currentUser } = useFamilyContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [simpleInterest, setSimpleInterest] = useState('');
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [selectedPathways, setSelectedPathways] = useState([]);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [planGenerated, setPlanGenerated] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  
  // Initialize form with project data
  const [frameworkData, setFrameworkData] = useState({
    title: '',
    complexity: '',
    coreProblem: '',
    whyMatters: '',
    pathways: {},
    gettingStartedSteps: []
  });

  useEffect(() => {
    if (projectIdea) {
      // Parse getting started steps
      const parseGettingStartedSteps = (text) => {
        if (!text) return [];
        
        const steps = [];
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);
        
        let currentStep = null;
        
        for (let line of lines) {
          const stepMatch = line.match(/^(\d+)\.\s*(.+)$/);
          
          if (stepMatch) {
            if (currentStep) {
              steps.push(currentStep);
            }
            
            currentStep = {
              number: parseInt(stepMatch[1]),
              title: stepMatch[2].trim(),
              description: ''
            };
          } else if (currentStep && line) {
            currentStep.description += (currentStep.description ? ' ' : '') + line;
          }
        }
        
        // Add the last step
        if (currentStep) {
          steps.push(currentStep);
        }
        
        return steps;
      };

      setFrameworkData({
        title: projectIdea.title || '',
        complexity: projectIdea.complexity || '',
        coreProblem: projectIdea.core_problem || projectIdea.coreProblem || '',
        whyMatters: projectIdea.real_world_context || projectIdea.realWorldContext || '',
        pathways: projectIdea.pathways || {},
        gettingStartedSteps: parseGettingStartedSteps(projectIdea.getting_started_steps || projectIdea.gettingStartedSteps || ''),
        recommendedTrack: projectIdea.recommended_track || projectIdea.recommendedTrack || ''
      });

      // Set initial recommended pathway selection
      const recommended = projectIdea.recommended_track || projectIdea.recommendedTrack;
      if (recommended) {
        setSelectedPathways([recommended]);
      } else {
        // Default to first enabled pathway
        const enabledPathways = Object.entries(projectIdea.pathways || {})
          .filter(([, data]) => data.enabled)
          .map(([key]) => key);
        if (enabledPathways.length > 0) {
          setSelectedPathways([enabledPathways[0]]);
        }
      }
    }
  }, [projectIdea]);

  const toggleStep = (stepIndex) => {
    setSelectedSteps(prev => 
      prev.includes(stepIndex) 
        ? prev.filter(i => i !== stepIndex)
        : [...prev, stepIndex]
    );
  };

  const togglePathway = (pathway) => {
    setSelectedPathways(prev => 
      prev.includes(pathway) 
        ? prev.filter(p => p !== pathway)
        : [...prev, pathway]
    );
  };

  const generatePlan = async () => {
    setIsGeneratingPlan(true);
    
    // Simulate AI generation with realistic plan based on selections
    setTimeout(() => {
      const plan = {
        phases: [
          {
            id: 1,
            title: "Define & Scope",
            intensity: "Foundation",
            description: "Lock in your vision and establish success metrics for the challenges ahead.",
            keyActivities: ["Finalize project specifications", "Define success criteria", "Set timeline goals"]
          },
          {
            id: 2, 
            title: "Research & Discovery",
            intensity: "Investigation",
            description: "Hunt for insights through hands-on research - this is where breakthrough ideas emerge.",
            keyActivities: selectedSteps.some(i => frameworkData.gettingStartedSteps[i]?.title?.toLowerCase().includes('research'))
              ? ["Deep-dive problem analysis", "User research interviews", "Market landscape mapping", "Technical feasibility study"]
              : ["Problem context research", "User needs analysis", "Existing solution review", "Constraint identification"]
          },
          {
            id: 3,
            title: "Creative Development", 
            intensity: "Innovation",
            description: "Generate and test multiple concepts using creative constraints and methodical exploration.",
            keyActivities: selectedPathways.includes('design') 
              ? ["User-centered design ideation", "Prototype sketching", "Concept validation", "Design iteration"]
              : ["Solution brainstorming", "Concept development", "Initial prototyping", "Feasibility testing"]
          },
          {
            id: 4,
            title: "Build & Validate",
            intensity: "Engineering", 
            description: "Create working solutions and validate with real users under realistic conditions.",
            keyActivities: selectedPathways.includes('technical')
              ? ["Technical prototype development", "Performance testing", "User acceptance testing", "Engineering validation"]
              : ["Solution implementation", "Real-world testing", "Feedback collection", "Iteration cycles"]
          },
          {
            id: 5,
            title: "Optimize & Document",
            intensity: "Professional",
            description: "Refine your solution and create comprehensive documentation of your process.",
            keyActivities: ["Final optimization", "Process documentation", "Impact assessment", "Portfolio preparation"]
          },
          {
            id: 6,
            title: "Launch & Impact",
            intensity: "Delivery", 
            description: "Present your work and identify pathways for real-world implementation.",
            keyActivities: ["Stakeholder presentations", "Implementation planning", "Portfolio finalization", "Future roadmap"]
          }
        ]
      };
      
      setGeneratedPlan(plan);
      setIsGeneratingPlan(false);
      setPlanGenerated(true);
    }, 2000);
  };

  const getPathwayIcon = (pathway) => {
    const icons = {
      research: Brain,
      technical: Target, 
      design: Palette,
      business: TrendingUp
    };
    return icons[pathway];
  };

  const handleStartProject = () => {
    const projectConfig = {
      originalIdea: projectIdea,
      customization: {
        interest: simpleInterest,
        selectedActivities: selectedSteps,
        pathways: selectedPathways,
        plan: generatedPlan
      },
      userId: currentUser?.id
    };
    
    onStartProject(projectConfig);
  };

  const steps = [
    { id: 1, title: "Review Framework", description: "Analyze foundation research" },
    { id: 2, title: "Define Angle", description: "Set focus (optional)" },
    { id: 3, title: "Select Track(s)", description: "Choose methodology" },
    { id: 4, title: "Synthesize Plan", description: "Generate execution roadmap" },
    { id: 5, title: "Initialize Workspace", description: "Launch development environment" }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-6xl w-full min-h-[95vh] max-h-full my-4">
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Header */}
          <div className="bg-white border-b border-stone-200">
            <div className="max-w-4xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Set Up Your Project</h1>
                  <p className="text-stone-600 mt-1">{frameworkData.title}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-stone-500">
                    Step {currentStep} of {steps.length}
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="bg-white border-b border-stone-200">
            <div className="max-w-4xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                      currentStep > step.id 
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : currentStep === step.id
                        ? 'border-teal-600 text-teal-600'
                        : 'border-stone-300 text-stone-400'
                    }`}>
                      {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                    </div>
                    <div className="ml-2 hidden md:block">
                      <div className={`text-sm font-medium ${currentStep >= step.id ? 'text-slate-900' : 'text-stone-500'}`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-stone-500">{step.description}</div>
                    </div>
                    {index < steps.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-stone-400 mx-2 md:mx-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Step 1: Review Framework */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Framework</h2>
                  <p className="text-stone-600">Foundation research and strategic options for this challenge</p>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                      {frameworkData.complexity || 'Intermediate'} Project
                    </span>
                    <span className="text-stone-500">•</span>
                    <span className="text-sm text-stone-600">College-ready complexity</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">The Challenge</h3>
                  <p className="text-stone-700 mb-4">{frameworkData.coreProblem}</p>
                  
                  <h4 className="font-medium text-stone-900 mb-2">Why This Matters</h4>
                  <p className="text-stone-600">{frameworkData.whyMatters}</p>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Available Approaches</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(frameworkData.pathways).filter(([, data]) => data.enabled).map(([key]) => {
                      const IconComponent = getPathwayIcon(key);
                      const isRecommended = frameworkData.recommendedTrack === key;
                      return (
                        <div key={key} className={`p-3 rounded-lg border text-center ${
                          isRecommended ? 'border-azure-200 bg-azure-50' : 'border-stone-200'
                        }`}>
                          <IconComponent className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                          <div className="font-medium text-sm text-slate-900 capitalize">{key}</div>
                          {isRecommended && (
                            <div className="text-xs text-azure-700 mt-1">Recommended</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {frameworkData.gettingStartedSteps.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200">
                    <button 
                      onClick={() => setShowGettingStarted(!showGettingStarted)}
                      className="w-full p-4 text-left hover:bg-stone-50 transition-colors rounded-xl"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-slate-900">Getting Started Activities</h3>
                        <ChevronRight className={`w-5 h-5 text-stone-400 transform transition-transform ${showGettingStarted ? 'rotate-90' : ''}`} />
                      </div>
                      <p className="text-sm text-stone-600 mt-1">Preview the hands-on activities you'll be doing</p>
                    </button>
                    
                    {showGettingStarted && (
                      <div className="px-4 pb-4">
                        <div className="border-t border-stone-200 pt-4 space-y-4">
                          {frameworkData.gettingStartedSteps.map((step, index) => (
                            <div key={index} className="flex gap-3">
                              <div className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                                {step.number}
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{step.title}</h4>
                                <p className="text-sm text-stone-600 mt-1">{step.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <h4 className="font-medium text-teal-900 mb-2">Ready to Customize</h4>
                  <p className="text-sm text-teal-800">
                    This framework provides the foundation. You'll choose your approach and get a customized execution plan.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Define Approach */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">What Draws You In?</h2>
                  <p className="text-stone-600">Optional: highlight what interests you most (helps customize your plan)</p>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Interest Check</h3>
                  <input
                    type="text"
                    value={simpleInterest}
                    onChange={(e) => setSimpleInterest(e.target.value)}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="e.g., 'testing with real users', 'the engineering challenge', 'building something useful'"
                  />
                  <p className="text-sm text-stone-500 mt-2">What aspect of this project sounds most interesting to you?</p>
                </div>

                {frameworkData.gettingStartedSteps.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Activities That Sound Interesting</h3>
                    <p className="text-sm text-stone-600 mb-4">Check any that catch your attention (helps AI customize your plan)</p>
                    
                    <div className="space-y-3">
                      {frameworkData.gettingStartedSteps.map((step, index) => (
                        <div 
                          key={index}
                          onClick={() => toggleStep(index)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedSteps.includes(index)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 ${
                              selectedSteps.includes(index)
                                ? 'bg-teal-600 text-white'
                                : 'bg-stone-200 text-stone-600'
                            }`}>
                              {selectedSteps.includes(index) ? '✓' : step.number}
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{step.title}</h4>
                              <p className="text-sm text-stone-600 mt-1">{step.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex-1 bg-azure-50 border border-azure-200 rounded-lg p-4">
                    <h4 className="font-medium text-azure-900 mb-2">Skip This Step?</h4>
                    <p className="text-sm text-azure-800 mb-3">
                      The framework works great as-is. You can always refine your focus later.
                    </p>
                    <button 
                      onClick={() => setCurrentStep(3)}
                      className="text-sm text-azure-700 hover:text-azure-800 font-medium"
                    >
                      Skip to Track Selection →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Choose Focus */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Your Track(s)</h2>
                  <p className="text-stone-600">Choose one or more methodological approaches that align with your strengths and interests</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(frameworkData.pathways).filter(([, data]) => data.enabled).map(([key, pathway]) => {
                    const IconComponent = getPathwayIcon(key);
                    const isSelected = selectedPathways.includes(key);
                    const isRecommended = frameworkData.recommendedTrack === key;
                    return (
                      <div 
                        key={key}
                        onClick={() => togglePathway(key)}
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-600'
                          }`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <h3 className="font-semibold text-slate-900 capitalize">{key} Track</h3>
                              {isRecommended && (
                                <span className="px-2 py-0.5 bg-azure-100 text-azure-700 rounded-full text-xs font-medium">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-medium text-stone-700 mb-3">{pathway.focus}</p>
                            <div className="text-sm text-stone-600">
                              <p>{pathway.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                  <h4 className="font-medium text-azure-900 mb-2">Strategic Note</h4>
                  <p className="text-sm text-azure-800">
                    Most projects benefit from 1-2 focused tracks rather than attempting all approaches. 
                    The recommended track reflects the project's natural strengths, but select based on your capabilities and goals.
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Generate Plan */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Generate Your Battle Plan</h2>
                  <p className="text-stone-600">Analysis of your interests and methodology to create a custom execution strategy</p>
                </div>

                {!planGenerated ? (
                  <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Configuration Analysis</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="text-left">
                        <h4 className="font-medium text-stone-700 mb-3">Selected Methodology</h4>
                        <div className="space-y-2">
                          {selectedPathways.map(pathway => (
                            <div key={pathway} className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                              <span className="text-sm font-medium capitalize">{pathway} Track</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="text-left">
                        <h4 className="font-medium text-stone-700 mb-3">Interest Signals</h4>
                        <div className="space-y-1">
                          {simpleInterest && (
                            <div className="text-sm text-stone-600">Focus: "{simpleInterest}"</div>
                          )}
                          <div className="text-sm text-stone-600">
                            {selectedSteps.length} preferred activities selected
                          </div>
                          {selectedSteps.length > 0 && (
                            <div className="text-xs text-stone-500 mt-2">
                              Emphasizing: {selectedSteps.map(i => frameworkData.gettingStartedSteps[i]?.title?.split(':')[0]).join(', ')}
                            </div>
                          )}
                          {!simpleInterest && selectedSteps.length === 0 && (
                            <div className="text-sm text-stone-500">Using framework as-is</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {isGeneratingPlan ? (
                      <div className="space-y-4">
                        <Sparkles className="w-8 h-8 text-teal-600 mx-auto animate-pulse" />
                        <div className="space-y-2">
                          <p className="text-stone-600">Analyzing methodology patterns...</p>
                          <p className="text-stone-600">Incorporating activity preferences...</p>
                          <p className="text-stone-600">Optimizing phase sequence...</p>
                        </div>
                        <div className="w-64 h-2 bg-stone-200 rounded-full mx-auto overflow-hidden">
                          <div className="h-full bg-teal-600 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={generatePlan}
                        className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                      >
                        <Sparkles className="w-4 h-4 inline mr-2" />
                        Generate Custom Strategy
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg p-4 text-center">
                      <h3 className="font-semibold text-lg mb-2">Your Custom Strategy is Ready</h3>
                      <p className="text-sm opacity-90">
                        Optimized for {selectedPathways.join(' + ')} methodology
                        {selectedSteps.length > 0 && ` • Emphasizing ${selectedSteps.length} preferred activities`}
                      </p>
                    </div>

                    {generatedPlan?.phases.map((phase) => {
                      const isEmphasized = selectedSteps.some(stepIndex => {
                        const stepTitle = frameworkData.gettingStartedSteps[stepIndex]?.title?.toLowerCase() || '';
                        const phaseTitle = phase.title.toLowerCase();
                        return stepTitle.includes('research') && phaseTitle.includes('research') ||
                               stepTitle.includes('prototype') && phaseTitle.includes('build') ||
                               stepTitle.includes('test') && phaseTitle.includes('validate');
                      });
                      
                      return (
                        <div key={phase.id} className={`rounded-lg border p-4 ${
                          isEmphasized ? 'border-teal-500 bg-teal-50' : 'border-stone-200 bg-white'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold text-slate-900">Phase {phase.id}: {phase.title}</h4>
                              {isEmphasized && (
                                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                                  ⚡ Emphasis
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                              <Target className="w-4 h-4" />
                              {phase.intensity}
                            </div>
                          </div>
                          <p className="text-stone-700 mb-3">{phase.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {phase.keyActivities.map((activity, index) => (
                              <span key={index} className={`px-2 py-1 rounded text-sm ${
                                isEmphasized ? 'bg-teal-100 text-teal-800' : 'bg-stone-100 text-stone-700'
                              }`}>
                                {activity}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                      <h4 className="font-medium text-azure-900 mb-2">Strategy Notes</h4>
                      <p className="text-sm text-azure-800">
                        Your plan emphasizes the activities you selected as interesting. Phase intensity reflects the type of breakthrough work expected - 
                        adapt your pacing based on what you discover and the time you have available.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Ready to Start */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Workspace Ready</h2>
                  <p className="text-stone-600">Execution environment configured and ready for development</p>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Implementation Sequence</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                        1
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Initialize Project Workspace</h4>
                        <p className="text-sm text-stone-600">Begin with Phase 1: Define & Scope to finalize project parameters</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-stone-300 text-white rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                        2
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Execute Initial Activities</h4>
                        <p className="text-sm text-stone-600">Start with your selected methodology from the framework</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-stone-300 text-white rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                        3
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Document Development Process</h4>
                        <p className="text-sm text-stone-600">Use workspace tools to capture insights and build portfolio documentation</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl text-white p-6 text-center">
                  <h3 className="text-xl font-bold mb-2">Begin Development</h3>
                  <p className="mb-4 opacity-90">Workspace initialized with framework data and execution plan</p>
                  <button 
                    onClick={handleStartProject}
                    className="px-8 py-3 bg-white text-teal-600 rounded-lg hover:bg-stone-50 transition-colors font-semibold"
                  >
                    Launch Project Workspace
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-stone-200">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 1
                    ? 'text-stone-400 cursor-not-allowed'
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="text-sm text-stone-500">
                {currentStep} of {steps.length}
              </div>
              
              {currentStep < steps.length ? (
                <button
                  onClick={() => {
                    if (currentStep === 4 && !planGenerated) return;
                    setCurrentStep(Math.min(steps.length, currentStep + 1));
                  }}
                  disabled={currentStep === 4 && !planGenerated}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    (currentStep === 4 && !planGenerated)
                      ? 'text-stone-400 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleStartProject}
                  className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  Start Project
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}